import { Types } from './Types'

export class ObjectHelper {
  public static areEqual(a: object | [], b: object | []): boolean {
    return JSON.stringify(a) === JSON.stringify(b)
  }

  public static clone<T>(obj: T): T {
    return obj ? (JSON.parse(JSON.stringify(obj)) as T) : obj
  }

  // this method only does a copy of the container object
  public static copy<T>(obj: T | undefined, action?: (obj: T) => void): T {
    const o = { ...obj } as unknown as T
    if (action) {
      action(o)
    }
    return o
  }

  public static forceNonNullable<T>(obj: T | undefined, def?: object): T {
    return (obj ?? (def as unknown)) as T
  }

  public static map<T, TT>(
    source: T,
    mappingRules?: (source: Readonly<T>, dest: TT) => void,
    destAsSource?: (destAsSource: T) => void
  ): TT {
    const dest = Types.convertType<TT>({ ...source })

    if (mappingRules) {
      // This callback allows to perform custom mapping actions
      mappingRules(source, dest)
    }

    if (destAsSource) {
      // This callback allows to delete properties which were automatically mapped from source
      destAsSource(Types.convertType<T>(dest))
    }

    return dest
  }

  /**
   * Deep merge two objects.
   */
  public static mergeObjects<T extends object = object>(target: T, ...sources: T[]): T {
    if (!sources.length) {
      return target
    }
    const source: T | undefined = sources.shift()
    if (source === undefined) {
      return target
    }

    const isMergebleObject = (item: object) => this.isMergebleObject(item)
    const mergeObjects = (target: T, ...sources: T[]) => this.mergeObjects(target, ...sources)

    if (this.isMergebleObject(target) && this.isMergebleObject(source)) {
      Object.keys(source).forEach(function (key: string) {
        if (isMergebleObject(source[key as keyof T] as object)) {
          if (!target[key as keyof T]) {
            target[key as keyof T] = {} as T[keyof T]
          }
          mergeObjects(target[key as keyof T] as T, source[key as keyof T] as T)
        } else {
          target[key as keyof T] = source[key as keyof T]
        }
      })
    }

    return this.mergeObjects(target, ...sources)
  }

  private static isObject(item: object): boolean {
    return item !== null && typeof item === 'object'
  }

  private static isMergebleObject(item: object): boolean {
    return this.isObject(item) && !Array.isArray(item)
  }
}
